import { clipPlayLength, type TemplateDoc } from '../template';
import { audioGain } from './beats';
import { sceneTimeline } from './compile';
import { layerWindow } from './scenes';
import { docMotionSchema, type MotionTrack } from './schema';

/**
 * Everything audible in a video as flat segments: the music, voice-over and effect tracks, and
 * the sound of clips (one segment per loop). The editor preview, the browser renderer and the
 * server mix all play exactly this list, so they sound the same.
 */
export interface SoundSegment {
  id: string;
  kind: 'music' | 'voice' | 'sfx' | 'clip';
  src: string;
  /** When it starts in the video, ms. */
  start: number;
  /** How long it plays, ms of video time. */
  duration: number;
  /** Where in the file it starts, ms. */
  mediaStart: number;
  /** Playback speed (clips). */
  rate: number;
  volume: number;
  fadeIn: number;
  fadeOut: number;
  /** Lowered while a voice plays. */
  ducked: boolean;
}

/** Part of the video a track covers, ms. */
export function trackSpan(track: MotionTrack): { start: number; end: number } {
  const available = Math.max(0, track.duration - track.trimStart);
  const length = Math.min(available, track.length ?? available);
  return { start: track.start, end: track.start + length };
}

/** Part of a video `total` ms long the music covers, ms: by default all of it. */
export function musicSpan(
  audio: { start?: number; length?: number | null },
  total: number,
): { start: number; end: number } {
  const start = Math.min(audio.start ?? 0, total);
  const end = audio.length != null ? Math.min(total, start + audio.length) : total;
  return { start, end: Math.max(start, end) };
}

export function soundSegments(doc: TemplateDoc): SoundSegment[] {
  const motion = docMotionSchema.parse(doc.motion ?? {});
  const total = sceneTimeline(motion).duration;
  const out: SoundSegment[] = [];
  const music = motion.audio;
  const span = music && musicSpan(music, total);
  if (music && span && span.end - span.start >= 20)
    out.push({
      id: 'music',
      kind: 'music',
      src: music.src,
      start: span.start,
      duration: span.end - span.start,
      mediaStart: music.trimStart,
      rate: 1,
      volume: music.volume,
      fadeIn: music.fadeIn,
      fadeOut: music.fadeOut,
      ducked: true,
    });
  for (const t of motion.tracks) {
    const span = trackSpan(t);
    const end = Math.min(span.end, total);
    if (end - span.start < 20) continue;
    out.push({
      id: `track-${t.id}`,
      kind: t.kind,
      src: t.src,
      start: span.start,
      duration: end - span.start,
      mediaStart: t.trimStart,
      rate: 1,
      volume: t.volume,
      fadeIn: t.fadeIn,
      fadeOut: t.fadeOut,
      ducked: t.kind === 'music',
    });
  }
  for (const l of doc.layers) {
    if (l.type !== 'image' || !l.visible || !l.video || l.video.muted || !l.video.src) continue;
    const v = l.video;
    const [from, to] = layerWindow(doc, l);
    const pass = Math.max(20, clipPlayLength(v));
    for (let k = 0, start = from; start < to - 20 && k < 200; k++, start += pass) {
      out.push({
        id: `clip-${l.id}-${k}`,
        kind: 'clip',
        src: v.src,
        start,
        duration: Math.min(pass, to - start),
        mediaStart: v.trimStart,
        rate: v.speed || 1,
        volume: v.volume,
        fadeIn: 0,
        fadeOut: 0,
        ducked: false,
      });
      if (!v.loop) break;
    }
  }
  return out;
}

export interface GainPoint {
  t: number;
  g: number;
}

const DUCK_RAMP = 250;

/**
 * Ducking: the level of ducked sounds over time (1, or `duck` while a voice plays, with short
 * ramps). Piecewise linear, as points sorted by time.
 */
export function duckEnvelope(doc: TemplateDoc, segments = soundSegments(doc)): GainPoint[] {
  const motion = docMotionSchema.parse(doc.motion ?? {});
  const level = motion.duck;
  const voices = segments
    .filter((s) => s.kind === 'voice')
    .map((s) => [s.start, s.start + s.duration] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  if (level >= 1 || !voices.length) return [{ t: 0, g: 1 }];
  // Merge voices closer than two ramps: the music stays down between them
  const merged: Array<[number, number]> = [];
  for (const v of voices) {
    const last = merged[merged.length - 1];
    if (last && v[0] - last[1] < DUCK_RAMP * 2) last[1] = Math.max(last[1], v[1]);
    else merged.push([...v]);
  }
  const pts: GainPoint[] = [{ t: 0, g: 1 }];
  for (const [a, b] of merged) {
    const down = Math.max(0, a - DUCK_RAMP);
    if (down > pts[pts.length - 1].t) pts.push({ t: down, g: 1 });
    pts.push({ t: Math.max(a, pts[pts.length - 1].t), g: level });
    pts.push({ t: b, g: level });
    pts.push({ t: b + DUCK_RAMP, g: 1 });
  }
  return pts;
}

export function envelopeAt(points: GainPoint[], t: number): number {
  if (!points.length) return 1;
  if (t <= points[0].t) return points[0].g;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (t <= b.t) return b.t === a.t ? b.g : a.g + ((b.g - a.g) * (t - a.t)) / (b.t - a.t);
  }
  return points[points.length - 1].g;
}

/** Level of a segment at video time `ms` (volume, fades, ducking), 0..1. */
export function segmentGain(segment: SoundSegment, ms: number, duck: GainPoint[]): number {
  const local = ms - segment.start;
  if (local < 0 || local > segment.duration) return 0;
  const g = audioGain(segment, local, segment.duration);
  return segment.ducked ? g * envelopeAt(duck, ms) : g;
}
